import type { NetworkId } from '@kwenta/sdk/types'
import { formatNumber, truncateAddress } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import { useCallback, useMemo } from 'react'
import { AbstractionToken } from 'types/accountAbstraction'
import { useChainId } from 'wagmi'

import { getBaseUrl } from 'containers/Connector/blockExplorer'
import {
	selectAbstractionEthBalance,
	selectAbstractionUsdcBalance,
} from 'state/futures/common/selectors'
import {
	selectAbstractionDelegated,
	selectAbstractionTxCost,
	selectAbstractionUsedToken,
	selectIsSponsoredProvider,
	selectSessionActive,
	selectSessionInfo,
} from 'state/futures/selectors'
import { useAppSelector } from 'state/hooks'
import { selectAbstractionAddress } from 'state/wallet/selectors'

export default function useAbstractionInfo() {
	const chainId = useChainId()

	const ethBalance = useAppSelector(selectAbstractionEthBalance)
	const address = useAppSelector(selectAbstractionAddress)
	const truncatedAddress = truncateAddress(address ?? '')
	const sessionInfo = useAppSelector(selectSessionInfo)
	const isDelegated = useAppSelector(selectAbstractionDelegated)
	const usdcBalance = useAppSelector(selectAbstractionUsdcBalance)

	const isSessionActive = useAppSelector(selectSessionActive)
	const estimateTradeCost = useAppSelector(selectAbstractionTxCost)
	const isSponsoredProvider = useAppSelector(selectIsSponsoredProvider)
	const usedToken = useAppSelector(selectAbstractionUsedToken)

	const formattedBalance = useMemo(() => {
		return formatNumber(usedToken === AbstractionToken.USDC ? usdcBalance : ethBalance, {
			suggestDecimals: true,
		})
	}, [ethBalance, usedToken, usdcBalance])

	const openWallet = useCallback(() => {
		const url = getBaseUrl(chainId as NetworkId)
		window.open(`${url}/address/${address}`, '_blank')
	}, [address, chainId])

	const isLowBalance = useMemo(() => {
		if (isSponsoredProvider) {
			return false
		}
		return wei(formattedBalance).lt(estimateTradeCost)
	}, [formattedBalance, estimateTradeCost, isSponsoredProvider])

	const estNumTxs = useMemo(() => {
		try {
			return Math.floor(wei(formattedBalance).div(estimateTradeCost).toNumber())
		} catch (_e) {
			return 0
		}
	}, [formattedBalance, estimateTradeCost])

	return {
		isSponsoredProvider,
		formattedBalance,
		address,
		truncatedAddress,
		openWallet,
		sessionInfo,
		estNumTxs,
		usedToken,
		estimateTradeCost,
		isSessionActive,
		isLowBalance,
		isDelegated,
	}
}
